/** The circuit as its evidence: every border record, where an admin can get
 *  at them.
 *
 * `TrackMap` draws what was compiled — polylines, a road. This draws what it
 * was compiled FROM, one dot a metre, because that is what an edit is about:
 * which records the map should not draw, and where a border nobody drove
 * should go. A canvas and not SVG, since a long circuit is thirteen thousand
 * records and an SVG of them is thirteen thousand elements to restyle on
 * every lasso.
 *
 * Four tools. **Move** pans. **Exclude** drags a lasso round what should not
 * be there. **Draw** clicks anchors for a spline through them. **Bridge**
 * clicks the record either side of a gap. In every tool the wheel zooms about
 * the cursor and shift-drag pans, so nobody has to change tool to look closer.
 *
 * The arithmetic — view, hit tests, spline, connector — is the track-data
 * repository's own editor core, vendored, so a border drawn here is the one
 * its editor would have drawn. This file is the part that needs a browser.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';

import { isDrawnOnly } from '@/components/editor/editSession';
import type { ExclusionArea } from '@/components/editor/editSession';
import type { Segment } from '@/lib/types';
import {
  boundsOf,
  catmullRomPath,
  clampView,
  connectorPath,
  pathLength,
  panView,
  scaleBar,
  screenToWorld,
  viewForBounds,
  wheelZoomUnits,
  worldToScreen,
  zoomFactor,
  zoomView,
} from '@/vendor/track-editor-core.mjs';
import type { Bounds, BundleEdge, ManualKind, PlanPoint, ScreenPoint, Side, View } from '@/vendor/track-editor-core.mjs';

export type Tool = 'move' | 'exclude' | 'draw' | 'bridge';

export interface SketchInfo {
  /** What is being sketched, or null when nothing is. */
  tool: 'draw' | 'bridge' | null;
  anchors: number;
  metres: number;
  /** Whether committing would produce anything. */
  ready: boolean;
  /** Said under the tools when a click could not be used. */
  problem: string;
}

export const NO_SKETCH: SketchInfo = { tool: null, anchors: 0, metres: 0, ready: false, problem: '' };

export interface CommittedPath {
  path: PlanPoint[];
  side: Side;
  kind: ManualKind;
}

export interface HoverInfo {
  world: PlanPoint;
  edge: BundleEdge | null;
  excluded: boolean;
  drawnNow: boolean;
}

export interface EditorCanvasHandle {
  fit(): void;
  zoom(units: number): void;
  commit(): void;
  cancel(): void;
  lookAt(polygon: readonly (readonly [number, number])[]): void;
}

export interface EditorCanvasProps {
  edges: readonly BundleEdge[];
  /** Records drawn in this session, not yet anywhere but here. */
  drawn: readonly BundleEdge[];
  /** 1 where an area keeps `edges[i]` out. */
  mask: Uint8Array;
  drawnMask: Uint8Array;
  areas: readonly ExclusionArea[];
  /** Areas `main` already has: drawn quieter than the ones made just now. */
  baseAreaIds: ReadonlySet<string>;
  activeArea: string | null;
  /** A lasso waiting on its dialog, so it stays on screen while it is decided. */
  pendingArea: readonly (readonly [number, number])[] | null;
  tool: Tool;
  sides: Record<Side, boolean>;
  drawSide: Side;
  drawKind: ManualKind;
  /** 0 is the straight line across a gap; 1 follows the headings out. */
  tension: number;
  /** Published gap spans, for finding what needs bridging. */
  gaps: readonly Segment[];
  preview: { raw: PlanPoint[][]; smooth: PlanPoint[][] } | null;
  onLasso(world: PlanPoint[]): void;
  onPath(committed: CommittedPath): void;
  onSketch(info: SketchInfo): void;
  onHover(info: HoverInfo | null): void;
  height?: number | string;
}

const COLOUR = {
  L: '#9184d9',
  R: 'oklch(0.78 0.10 225)',
  drawn: 'oklch(0.75 0.11 150)',
  excluded: '#9397ab',
  area: 'oklch(0.68 0.16 20)',
  gap: 'oklch(0.78 0.12 75)',
  ink: '#e9e9ed',
  faint: '#9397ab',
  bg: '#161826',
} as const;

/** How close a click has to be to a record to mean that record, in pixels. */
const PICK_PX = 14;
/** A press that moves less than this is a click, not a drag. */
const CLICK_PX = 4;
const MANUAL: readonly string[] = ['wall', 'runoff', 'edge'];

/**
 * The portal draws a circuit with world z negated — north up, as `TrackMap`
 * and every outline on the site has it — and the vendored core draws it with
 * z down the screen, as the datalogger does. An admin going from a circuit's
 * page to its editor should not have to turn the circuit over in their head,
 * so this canvas is the portal's way up: the view lives in drawing space
 * (x, -z), and these two are the only doors between it and the world.
 */
const flip = (point: PlanPoint): PlanPoint => ({ x: point.x, z: -point.z });
const toScreenIn = (point: PlanPoint, view: View, width: number, height: number): ScreenPoint =>
  worldToScreen(flip(point), view, width, height);
const toWorldIn = (point: ScreenPoint, view: View, width: number, height: number): PlanPoint =>
  flip(screenToWorld(point, view, width, height));

interface Drag {
  id: number;
  mode: 'pan' | 'lasso' | 'click';
  start: ScreenPoint;
  last: ScreenPoint;
  moved: boolean;
}

export const EditorCanvas = forwardRef<EditorCanvasHandle, EditorCanvasProps>(function EditorCanvas(
  props,
  ref,
) {
  const { edges, drawn, mask, drawnMask, areas, baseAreaIds, activeArea, pendingArea, tool, sides } = props;
  const { drawSide, drawKind, tension, gaps, preview, onLasso, onPath, onSketch, onHover, height = 560 } = props;

  const frame = useRef<HTMLDivElement | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [view, setView] = useState<View | null>(null);
  const [lasso, setLasso] = useState<ScreenPoint[]>([]);
  const [anchors, setAnchors] = useState<PlanPoint[]>([]);
  const [ends, setEnds] = useState<BundleEdge[]>([]);
  const [cursor, setCursor] = useState<ScreenPoint | null>(null);
  const [problem, setProblem] = useState('');
  const drag = useRef<Drag | null>(null);

  const bounds = useMemo<Bounds | null>(() => boundsOf([...edges, ...drawn].map(flip)), [edges, drawn]);

  // ── size, and the first fit ──────────────────────────────────────────────
  useEffect(() => {
    const element = frame.current;
    if (!element) return undefined;
    const read = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const fitted = useCallback(
    () => (bounds && size.width > 0 ? viewForBounds(bounds, size.width, size.height, 36) : null),
    [bounds, size.width, size.height],
  );

  useEffect(() => {
    // Fitted once there is something to fit and somewhere to fit it; after
    // that the view is the admin's, and a resize keeps what they were
    // looking at in the middle rather than throwing them back out.
    setView((current) => {
      const fit = fitted();
      if (!fit) return current;
      return current ? { ...current, baseScale: fit.scale } : fit;
    });
  }, [fitted]);

  // A change of tool abandons whatever the last one was half way through.
  useEffect(() => {
    setAnchors([]);
    setEnds([]);
    setLasso([]);
    setProblem('');
  }, [tool]);

  // ── the sketch: what Draw or Bridge would commit ─────────────────────────
  const sketch = useMemo<PlanPoint[]>(() => {
    if (tool === 'draw') return anchors.length >= 2 ? catmullRomPath(anchors) : [];
    const [a, b] = ends;
    return tool === 'bridge' && a && b ? connectorPath(a, b, { tension }) : [];
  }, [tool, anchors, ends, tension]);

  useEffect(() => {
    const sketching = tool === 'draw' || tool === 'bridge' ? tool : null;
    onSketch({
      tool: sketching,
      anchors: tool === 'draw' ? anchors.length : ends.length,
      metres: sketch.length >= 2 ? pathLength(sketch) : 0,
      ready: sketch.length >= 2,
      problem,
    });
  }, [tool, anchors.length, ends.length, sketch, problem, onSketch]);

  const cancel = useCallback(() => {
    setAnchors([]);
    setEnds([]);
    setLasso([]);
    setProblem('');
  }, []);

  const commit = useCallback(() => {
    if (sketch.length < 2) return;
    if (tool === 'draw') {
      onPath({ path: sketch, side: drawSide, kind: drawKind });
    } else if (tool === 'bridge') {
      const first = ends[0];
      if (!first) return;
      // A bridge is what it joins: its side, and its kind when that was
      // somebody's judgement. An inferred kind becomes a plain edge — nobody
      // can infer anything about a metre nobody drove.
      const kind = (MANUAL.includes(first.kind) ? first.kind : 'edge') as ManualKind;
      onPath({ path: sketch, side: first.side, kind });
    }
    cancel();
  }, [sketch, tool, ends, drawSide, drawKind, onPath, cancel]);

  const change = useCallback(
    (next: (current: View) => View) => {
      setView((current) => (current ? clampView(next(current), bounds, size.width, size.height) : current));
    },
    [bounds, size.width, size.height],
  );

  useImperativeHandle(
    ref,
    () => ({
      fit: () => setView(fitted()),
      zoom: (units) =>
        change((current) =>
          zoomView(current, { x: size.width / 2, y: size.height / 2 }, zoomFactor(units), size.width, size.height),
        ),
      commit,
      cancel,
      lookAt: (polygon) => {
        const box = boundsOf(polygon.map(([x, z]) => ({ x, z: -z })));
        const fit = fitted();
        if (!box || !fit) return;
        const margin = Math.max(20, (box.maxX - box.minX + box.maxZ - box.minZ) / 2);
        const grown = { minX: box.minX - margin, maxX: box.maxX + margin, minZ: box.minZ - margin, maxZ: box.maxZ + margin };
        setView(viewForBounds(grown, size.width, size.height, 36, fit.scale));
      },
    }),
    [fitted, change, commit, cancel, size.width, size.height],
  );

  // ── picking ──────────────────────────────────────────────────────────────
  const pick = useCallback(
    (at: ScreenPoint): { edge: BundleEdge; excluded: boolean; drawnNow: boolean } | null => {
      if (!view) return null;
      let best: { edge: BundleEdge; excluded: boolean; drawnNow: boolean } | null = null;
      let bestSquared = PICK_PX * PICK_PX;
      const consider = (list: readonly BundleEdge[], flags: Uint8Array, drawnNow: boolean) => {
        for (let i = 0; i < list.length; i += 1) {
          const edge = list[i] as BundleEdge;
          if (!sides[edge.side]) continue;
          const screen = toScreenIn(edge, view, size.width, size.height);
          const squared = (screen.x - at.x) ** 2 + (screen.y - at.y) ** 2;
          if (squared <= bestSquared) {
            bestSquared = squared;
            best = { edge, excluded: flags[i] === 1, drawnNow };
          }
        }
      };
      consider(edges, mask, false);
      consider(drawn, drawnMask, true);
      return best;
    },
    [view, edges, drawn, mask, drawnMask, sides, size.width, size.height],
  );

  // ── the wheel ────────────────────────────────────────────────────────────
  useEffect(() => {
    const element = frame.current;
    if (!element) return undefined;
    // React registers `wheel` as passive, and a passive listener cannot stop
    // the page scrolling underneath the circuit it is zooming.
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const box = element.getBoundingClientRect();
      const units = wheelZoomUnits(event, box.height);
      if (units === 0) return;
      change((current) =>
        zoomView(
          current,
          { x: event.clientX - box.left, y: event.clientY - box.top },
          zoomFactor(units),
          size.width,
          size.height,
        ),
      );
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [change, size.width, size.height]);

  // ── the pointer ──────────────────────────────────────────────────────────
  const local = (event: ReactPointerEvent<HTMLDivElement>): ScreenPoint => {
    const box = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - box.left, y: event.clientY - box.top };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !view) return;
    // Capture keeps a drag that leaves the canvas ours. It is a nicety: a
    // pointer the browser will not capture must not cost the whole gesture.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Dragged without capture; `buttons` below still notices a lost release.
    }
    event.currentTarget.focus({ preventScroll: true });
    const at = local(event);
    const mode: Drag['mode'] = event.shiftKey || tool === 'move' ? 'pan' : tool === 'exclude' ? 'lasso' : 'click';
    drag.current = { id: event.pointerId, mode, start: at, last: at, moved: false };
    if (mode === 'lasso') setLasso([at]);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const at = local(event);
    setCursor(at);
    const held = drag.current;
    if (!held || held.id !== event.pointerId) {
      if (view) {
        const found = pick(at);
        onHover({
          world: toWorldIn(at, view, size.width, size.height),
          edge: found?.edge ?? null,
          excluded: found?.excluded ?? false,
          drawnNow: found?.drawnNow ?? false,
        });
      }
      return;
    }
    // A release can be lost — let go over another window — and a canvas that
    // went on following a pointer nobody holds is unusable until reload.
    if ((event.buttons & 1) === 0) {
      drag.current = null;
      setLasso([]);
      return;
    }
    if (Math.hypot(at.x - held.start.x, at.y - held.start.y) > CLICK_PX) held.moved = true;
    if (held.mode === 'lasso') {
      const previous = held.last;
      if (Math.hypot(at.x - previous.x, at.y - previous.y) >= 3) {
        held.last = at;
        setLasso((points) => [...points, at]);
      }
      return;
    }
    // A click that turned into a drag is a pan: nobody should have to change
    // tool to look along the border they are drawing.
    if (held.mode === 'pan' || held.moved) {
      const dx = at.x - held.last.x;
      const dy = at.y - held.last.y;
      held.last = at;
      change((current) => panView(current, dx, dy));
    }
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const held = drag.current;
    if (!held || held.id !== event.pointerId || !view) return;
    drag.current = null;
    const at = local(event);

    if (held.mode === 'lasso') {
      const outline = [...lasso, at];
      setLasso([]);
      if (outline.length >= 3 && held.moved) {
        onLasso(outline.map((point) => toWorldIn(point, view, size.width, size.height)));
      }
      return;
    }
    if (held.mode !== 'click' || held.moved) return;

    if (tool === 'draw') {
      setAnchors((current) => [...current, toWorldIn(at, view, size.width, size.height)]);
      return;
    }
    if (tool === 'bridge') {
      const found = pick(at);
      if (!found) {
        setProblem('Click a border record: the last one before the gap, then the first one after it.');
        return;
      }
      const first = ends[0];
      if (ends.length !== 1 || !first) {
        setProblem('');
        setEnds([found.edge]);
        return;
      }
      if (found.edge.side !== first.side) {
        // Joining the left border to the right would draw a line across the
        // road and file it as one border.
        setProblem(`That record is on the ${found.edge.side === 'L' ? 'left' : 'right'} border. A bridge joins two records on the same side.`);
        return;
      }
      if (found.edge === first) return;
      setProblem('');
      setEnds([first, found.edge]);
    }
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    } else if (event.key === 'Backspace' && tool === 'draw') {
      event.preventDefault();
      setAnchors((current) => current.slice(0, -1));
    } else if (event.key === 'Backspace' && tool === 'bridge') {
      event.preventDefault();
      setEnds((current) => current.slice(0, -1));
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      change((current) => zoomView(current, cursor ?? { x: size.width / 2, y: size.height / 2 }, zoomFactor(1), size.width, size.height));
    } else if (event.key === '-') {
      event.preventDefault();
      change((current) => zoomView(current, cursor ?? { x: size.width / 2, y: size.height / 2 }, zoomFactor(-1), size.width, size.height));
    } else if (event.key === '0') {
      event.preventDefault();
      setView(fitted());
    }
  };

  // ── drawing ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const element = canvas.current;
    if (!element || !view || size.width === 0) return;
    const ratio = window.devicePixelRatio || 1;
    if (element.width !== Math.round(size.width * ratio) || element.height !== Math.round(size.height * ratio)) {
      element.width = Math.round(size.width * ratio);
      element.height = Math.round(size.height * ratio);
    }
    const ctx = element.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);

    const toScreen = (point: PlanPoint) => toScreenIn(point, view, size.width, size.height);
    const onScreen = (p: ScreenPoint) => p.x > -8 && p.y > -8 && p.x < size.width + 8 && p.y < size.height + 8;
    // A dot a metre, never smaller than can be seen and never so large that
    // two neighbours merge: a record is a metre, so a metre is the ceiling.
    const dot = Math.max(1.4, Math.min(5, view.scale * 0.42));

    const outline = (polygon: readonly (readonly [number, number])[]) => {
      ctx.beginPath();
      polygon.forEach(([x, z], index) => {
        const p = toScreen({ x, z });
        if (index === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.closePath();
    };

    // Gaps the published map has: what Bridge is for.
    ctx.save();
    ctx.strokeStyle = COLOUR.gap;
    ctx.lineWidth = 2;
    ctx.setLineDash([3, 5]);
    ctx.globalAlpha = 0.85;
    for (const gap of gaps) {
      const a = toScreen({ x: gap[0], z: gap[1] });
      const b = toScreen({ x: gap[2], z: gap[3] });
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    ctx.restore();

    // Areas, under the records they keep out.
    for (const area of areas) {
      const fresh = !baseAreaIds.has(area.id);
      const active = area.id === activeArea;
      ctx.save();
      outline(area.polygon);
      ctx.globalAlpha = active ? 0.26 : fresh ? 0.16 : 0.09;
      ctx.fillStyle = COLOUR.area;
      ctx.fill();
      ctx.globalAlpha = active ? 1 : fresh ? 0.9 : 0.5;
      ctx.strokeStyle = COLOUR.area;
      ctx.lineWidth = active ? 2 : 1.25;
      if (!fresh) ctx.setLineDash([5, 4]);
      ctx.stroke();
      ctx.restore();
    }
    if (pendingArea) {
      ctx.save();
      outline(pendingArea);
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = COLOUR.area;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = COLOUR.ink;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.stroke();
      ctx.restore();
    }

    // The smoothing preview: the border as recorded, faint, and as the
    // compiler's smoother would leave it, on top.
    if (preview) {
      const stroke = (runs: PlanPoint[][], colour: string, alpha: number, width: number) => {
        ctx.save();
        ctx.strokeStyle = colour;
        ctx.globalAlpha = alpha;
        ctx.lineWidth = width;
        ctx.lineJoin = 'round';
        for (const run of runs) {
          ctx.beginPath();
          run.forEach((point, index) => {
            const p = toScreen(point);
            if (index === 0) ctx.moveTo(p.x, p.y);
            else ctx.lineTo(p.x, p.y);
          });
          ctx.stroke();
        }
        ctx.restore();
      };
      stroke(preview.raw, COLOUR.faint, 0.55, 1);
      stroke(preview.smooth, COLOUR.ink, 0.95, 1.75);
    }

    // Records. Excluded ones first and hollow, so what is kept reads on top.
    const records = (list: readonly BundleEdge[], flags: Uint8Array, excluded: boolean, colour?: string) => {
      for (let i = 0; i < list.length; i += 1) {
        const edge = list[i] as BundleEdge;
        if ((flags[i] === 1) !== excluded || !sides[edge.side]) continue;
        const p = toScreen(edge);
        if (!onScreen(p)) continue;
        if (excluded) {
          ctx.strokeStyle = COLOUR.excluded;
          ctx.globalAlpha = 0.55;
          ctx.lineWidth = 1;
          ctx.strokeRect(p.x - dot, p.y - dot, dot * 2, dot * 2);
        } else {
          // Drawn by an earlier edit: the same green as what is drawn now,
          // quieter, so a bridge reads as a bridge and not as a survey.
          const earlier = colour === undefined && isDrawnOnly(edge);
          ctx.fillStyle = colour ?? (earlier ? COLOUR.drawn : COLOUR[edge.side]);
          ctx.globalAlpha = earlier ? 0.7 : 1;
          ctx.fillRect(p.x - dot, p.y - dot, dot * 2, dot * 2);
        }
      }
      ctx.globalAlpha = 1;
    };
    records(edges, mask, true);
    records(drawn, drawnMask, true);
    records(edges, mask, false);
    records(drawn, drawnMask, false, COLOUR.drawn);

    // The sketch.
    if (sketch.length >= 2) {
      ctx.save();
      ctx.strokeStyle = COLOUR.drawn;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      sketch.forEach((point, index) => {
        const p = toScreen(point);
        if (index === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.stroke();
      ctx.restore();
    }
    for (const mark of tool === 'draw' ? anchors : ends) {
      const p = toScreen(mark);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
      ctx.fillStyle = COLOUR.bg;
      ctx.fill();
      ctx.strokeStyle = COLOUR.drawn;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    // Draw's next segment, to the cursor: where the next click would take it.
    const lastAnchor = anchors[anchors.length - 1];
    if (tool === 'draw' && lastAnchor && cursor && !drag.current) {
      const from = toScreen(lastAnchor);
      ctx.save();
      ctx.strokeStyle = COLOUR.drawn;
      ctx.globalAlpha = 0.5;
      ctx.setLineDash([2, 4]);
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(cursor.x, cursor.y);
      ctx.stroke();
      ctx.restore();
    }

    // The lasso in hand.
    if (lasso.length >= 2) {
      ctx.save();
      ctx.beginPath();
      lasso.forEach((p, index) => (index === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
      ctx.globalAlpha = 0.14;
      ctx.fillStyle = COLOUR.area;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = COLOUR.area;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.stroke();
      ctx.restore();
    }
  }, [view, size, edges, drawn, mask, drawnMask, areas, baseAreaIds, activeArea, pendingArea, sides, gaps, preview, sketch, anchors, ends, lasso, cursor, tool]);

  const bar = view ? scaleBar(view) : null;
  const cursorStyle = tool === 'move' ? 'grab' : tool === 'exclude' ? 'crosshair' : tool === 'draw' ? 'crosshair' : 'pointer';

  return (
    <div
      ref={frame}
      role="application"
      aria-label="Circuit evidence. Scroll to zoom, shift-drag to move, Enter to commit a drawn path, Escape to cancel."
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={() => {
        setCursor(null);
        onHover(null);
      }}
      onDoubleClick={() => {
        if (tool === 'draw') commit();
      }}
      onKeyDown={onKeyDown}
      // A press that starts here must not start a text selection that the
      // drag then sweeps across the page.
      onMouseDown={(event) => event.preventDefault()}
      style={{
        position: 'relative',
        height,
        borderRadius: 8,
        overflow: 'hidden',
        background: 'color-mix(in srgb, var(--color-bg) 70%, black)',
        cursor: cursorStyle,
        touchAction: 'none',
        userSelect: 'none',
        outlineOffset: -2,
      }}
    >
      <canvas ref={canvas} style={{ width: '100%', height: '100%', display: 'block' }} />
      {bar ? (
        <div
          aria-hidden="true"
          className="tabular-nums"
          style={{ position: 'absolute', left: 12, bottom: 10, fontSize: 11, color: 'var(--color-neutral-400)', pointerEvents: 'none' }}
        >
          <div
            style={{
              width: bar.pixels,
              height: 5,
              borderStyle: 'solid',
              borderColor: 'var(--color-neutral-500)',
              borderWidth: '0 1px 1px 1px',
              marginBottom: 3,
            }}
          />
          {bar.label}
        </div>
      ) : null}
    </div>
  );
});

export default EditorCanvas;
